import React, { useState, useEffect } from 'react';
import { businessCardService } from '../../services/api';
import { BusinessCard } from '../../types';
import BusinessCardForm from '../../components/BusinessCardForm';
import ImagePopup from '../../components/ImagePopup';

const BusinessCardsList: React.FC = () => {
  const [allCards, setAllCards] = useState<BusinessCard[]>([]);
  const [allTags, setAllTags] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allCompanies, setAllCompanies] = useState<{name: string; count: number}[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCard, setEditingCard] = useState<BusinessCard | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState<BusinessCard | null>(null);
  const [showImagePopup, setShowImagePopup] = useState(false);

  // モバイルデバイスの判定
  const [isMobile, setIsMobile] = useState(false);
  const [showImagesOnMobile, setShowImagesOnMobile] = useState(false); // モバイルでのデフォルトは非表示

  useEffect(() => {
    // モバイルデバイスの検出
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const mobileCheck = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(mobileCheck);

      // LocalStorageから設定を読み込み
      const savedImageSetting = localStorage.getItem('showImagesOnMobile');
      if (savedImageSetting !== null) {
        setShowImagesOnMobile(savedImageSetting === 'true');
      }
    };

    checkMobile();
    fetchCards();
    // fetchTags(); は fetchCards 内で実行されるため削除
  }, []);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const cardData = await businessCardService.getAll();
      console.log('Fetched cards:', cardData);
      if (cardData.length > 0) {
        console.log('🔥 First card mobile field:', cardData[0].mobile);
        console.log('🔥 First card fax field:', cardData[0].fax);
        console.log('🔥 First card website field:', cardData[0].website);
      }
      setAllCards(cardData);
      // カード取得後に使用されているタグと会社を抽出
      extractUsedTags(cardData);
      extractUsedCompanies(cardData);
    } catch (err: any) {
      console.error('Fetch cards error:', err);
      setError('名刺データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // クライアント側フィルタリング
  const filteredCards = React.useMemo(() => {
    let filtered = allCards;

    // キーワード検索
    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(card =>
        card.full_name?.toLowerCase().includes(keyword) ||
        card.company?.toLowerCase().includes(keyword) ||
        card.title?.toLowerCase().includes(keyword) ||
        card.email?.toLowerCase().includes(keyword) ||
        card.phone?.toLowerCase().includes(keyword)
      );
    }

    // タグフィルター
    if (selectedTags.length > 0) {
      filtered = filtered.filter(card => {
        if (!card.tags || !Array.isArray(card.tags)) return false;
        const cardTagNames = card.tags.map(tag =>
          typeof tag === 'string' ? tag : (tag?.name || '')
        );
        return selectedTags.some(selectedTag => cardTagNames.includes(selectedTag));
      });
    }

    // 会社フィルター
    if (selectedCompanies.length > 0) {
      filtered = filtered.filter(card => {
        return card.company && selectedCompanies.includes(card.company);
      });
    }

    return filtered;
  }, [allCards, searchKeyword, selectedTags, selectedCompanies]);

  // 実際に使用されているタグのみを抽出
  const extractUsedTags = (cards: BusinessCard[]) => {
    const usedTagsSet = new Set<string>();

    cards.forEach(card => {
      if (card.tags && Array.isArray(card.tags)) {
        card.tags.forEach(tag => {
          const tagName = typeof tag === 'string' ? tag : (tag?.name || '');
          if (tagName) {
            usedTagsSet.add(tagName);
          }
        });
      }
    });

    // タグオブジェクト形式に変換（既存のallTagsと同じ形式に合わせる）
    const usedTags = Array.from(usedTagsSet).map(tagName => ({ name: tagName }));
    setAllTags(usedTags);
  };

  // 会社名を抽出して件数をカウント
  const extractUsedCompanies = (cards: BusinessCard[]) => {
    const companyCount = new Map<string, number>();

    cards.forEach(card => {
      if (card.company) {
        companyCount.set(card.company, (companyCount.get(card.company) || 0) + 1);
      }
    });

    // 件数の多い順にソート
    const companies = Array.from(companyCount.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    setAllCompanies(companies);
  };

  const fetchTags = async () => {
    // 現在はallCardsから使用されているタグを抽出
    // 将来的にはサーバーサイドで使用中タグのみ返すAPIを使用可能
    extractUsedTags(allCards);
  };

  const handleCreateCard = async (cardData: Partial<BusinessCard>) => {
    setSaveLoading(true);
    try {
      const newCard = await businessCardService.create(cardData);
      console.log('New card created:', newCard);
      const updatedCards = [...allCards, newCard];
      console.log('Updated cards list:', updatedCards);
      setAllCards(updatedCards);
      extractUsedTags(updatedCards);
      extractUsedCompanies(updatedCards);
      setShowCreateForm(false);
    } catch (err: any) {
      console.error('Create error:', err);
      setError('名刺の作成に失敗しました。');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateCard = async (cardData: Partial<BusinessCard>) => {
    if (!editingCard) return;

    setSaveLoading(true);
    try {
      const updatedCard = await businessCardService.update(editingCard.id, cardData);
      console.log('Card updated:', updatedCard);
      const updatedCards = allCards.map(card => card.id === editingCard.id ? updatedCard : card);
      console.log('Updated cards list:', updatedCards);
      setAllCards(updatedCards);
      extractUsedTags(updatedCards);
      extractUsedCompanies(updatedCards);
      setEditingCard(null);
    } catch (err: any) {
      setError('名刺の更新に失敗しました。');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!window.confirm('この名刺を削除してもよろしいですか？')) return;

    try {
      await businessCardService.delete(cardId);
      const updatedCards = allCards.filter(card => card.id !== cardId);
      setAllCards(updatedCards);
      extractUsedTags(updatedCards);
      extractUsedCompanies(updatedCards);
    } catch (err: any) {
      setError('名刺の削除に失敗しました。');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('bizcard_access_token');
    localStorage.removeItem('bizcard_refresh_token');
    window.location.href = '/login';
  };

  const handleImageClick = (card: BusinessCard) => {
    setSelectedCard(card);
    setShowImagePopup(true);
  };

  const handleCloseImagePopup = () => {
    setShowImagePopup(false);
    setSelectedCard(null);
  };

  const toggleTagFilter = (tagName: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return prev.filter(tag => tag !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
  };

  const clearTagFilter = () => {
    setSelectedTags([]);
  };

  const toggleCompanyFilter = (companyName: string) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyName)) {
        return prev.filter(company => company !== companyName);
      } else {
        return [...prev, companyName];
      }
    });
  };

  const clearCompanyFilter = () => {
    setSelectedCompanies([]);
  };

  const toggleMobileImages = () => {
    const newValue = !showImagesOnMobile;
    setShowImagesOnMobile(newValue);
    localStorage.setItem('showImagesOnMobile', newValue.toString());
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        読み込み中...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* ヘッダーメニュー */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #dee2e6',
        padding: '10px 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px'
        }}>
          <h1 style={{ margin: 0, color: '#007bff', fontSize: '24px' }}>
            名刺管理システム
          </h1>

          {/* メニューボタン */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              + 新規登録
            </button>
            <button
              onClick={() => window.location.href = '/users'}
              style={{
                padding: '8px 16px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              👥 ユーザー管理
            </button>
            {isMobile && (
              <button
                onClick={toggleMobileImages}
                style={{
                  padding: '8px 16px',
                  backgroundColor: showImagesOnMobile ? '#ffc107' : '#6c757d',
                  color: showImagesOnMobile ? '#000' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🖼️ {showImagesOnMobile ? '画像表示中' : '画像非表示'}
              </button>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* 検索・フィルター */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #ddd'
        }}>
          {/* 検索バー */}
          <input
            type="text"
            placeholder="名刺を検索..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              marginBottom: '15px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />

          {/* 会社フィルター */}
          {allCompanies.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold', color: '#333' }}>
                  会社フィルター {selectedCompanies.length > 0 && `(${selectedCompanies.length}個選択中)`}
                </label>
                {selectedCompanies.length > 0 && (
                  <button
                    onClick={clearCompanyFilter}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    クリア
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {allCompanies.map(company => (
                  <button
                    key={company.name}
                    onClick={() => toggleCompanyFilter(company.name)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #dee2e6',
                      borderRadius: '15px',
                      backgroundColor: selectedCompanies.includes(company.name) ? '#28a745' : '#f8f9fa',
                      color: selectedCompanies.includes(company.name) ? 'white' : '#495057',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span>{company.name}</span>
                    <span style={{
                      backgroundColor: selectedCompanies.includes(company.name) ? 'rgba(255,255,255,0.3)' : '#6c757d',
                      color: selectedCompanies.includes(company.name) ? 'white' : 'white',
                      borderRadius: '10px',
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      {company.count}
                    </span>
                  </button>
                ))}
              </div>

              {selectedCompanies.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <small style={{ color: '#666' }}>選択中: {selectedCompanies.join(', ')}</small>
                </div>
              )}
            </div>
          )}

          {/* タグフィルター */}
          {allTags.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold', color: '#333' }}>
                  タグフィルター {selectedTags.length > 0 && `(${selectedTags.length}個選択中)`}
                </label>
                {selectedTags.length > 0 && (
                  <button
                    onClick={clearTagFilter}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    クリア
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {allTags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTagFilter(tag.name)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #dee2e6',
                      borderRadius: '15px',
                      backgroundColor: selectedTags.includes(tag.name) ? '#007bff' : '#f8f9fa',
                      color: selectedTags.includes(tag.name) ? 'white' : '#495057',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>

              {selectedTags.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <small style={{ color: '#666' }}>選択中: {selectedTags.join(', ')}</small>
                </div>
              )}
            </div>
          )}
        </div>

      {filteredCards.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '50px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px dashed #ddd'
        }}>
          <p style={{ color: '#666', fontSize: '18px' }}>
            {allCards.length === 0 ? '名刺がまだ登録されていません' : '検索条件に一致する名刺がありません'}
          </p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                {(!isMobile || showImagesOnMobile) && (
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', width: '250px' }}>
                    画像
                  </th>
                )}
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', width: '200px' }}>
                  会社名・役職・氏名
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  連絡先・住所
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', width: '180px' }}>
                  メモ
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd', width: '120px' }}>
                  タグ
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #ddd', width: '80px' }}>
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCards.map((card) => (
                <tr key={card.id} style={{ borderBottom: '1px solid #eee' }}>
                  {(!isMobile || showImagesOnMobile) && (
                    <td style={{ padding: '6px 12px', overflow: 'hidden', height: '200px', textAlign: 'center' }}>
                      {card.image_path ? (
                      <img
                        src={`/media/${card.image_path}`}
                        alt={`${card.full_name}の名刺`}
                        style={{
                          width: '180px',
                          height: '220px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          transform: 'rotate(90deg)'
                        }}
                        onClick={() => handleImageClick(card)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.8';
                          e.currentTarget.style.transform = 'rotate(90deg) scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.transform = 'rotate(90deg) scale(1)';
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '180px',
                          height: '220px',
                          backgroundColor: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          color: '#999',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleImageClick(card)}
                      >
                        画像なし
                        <br />
                        <small style={{ fontSize: '10px' }}>(クリックで詳細)</small>
                      </div>
                    )}
                    </td>
                  )}
                  <td style={{ padding: '12px', width: '140px' }}>
                    <div style={{ fontSize: '13px', color: '#333', marginBottom: '2px', fontWeight: 'bold' }}>
                      {card.company || '-'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>
                      {card.title || '-'}
                    </div>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>
                      {card.full_name}{card.full_name_reading && `（${card.full_name_reading}）`}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                      {new Date(card.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {card.phone && (
                      <div style={{ fontSize: '13px', marginBottom: '3px' }}>
                        📞 <a href={`tel:${card.phone.replace(/[-\s]/g, '')}`} style={{ color: '#007bff', textDecoration: 'none' }}>{card.phone}</a>
                      </div>
                    )}
                    {card.mobile && (
                      <div style={{ fontSize: '13px', marginBottom: '3px' }}>
                        📲 <a href={`tel:${card.mobile.replace(/[-\s]/g, '')}`} style={{ color: '#007bff', textDecoration: 'none' }}>{card.mobile}</a>
                      </div>
                    )}
                    {card.fax && <div style={{ fontSize: '13px', marginBottom: '3px' }}>📄 {card.fax}</div>}
                    {card.email && <div style={{ fontSize: '13px', marginBottom: '3px' }}>✉️ {card.email}</div>}
                    {card.website && <div style={{ fontSize: '13px', marginBottom: '3px' }}>🌐 {card.website}</div>}
                    {card.address && <div style={{ fontSize: '13px', lineHeight: '1.4', color: '#666' }}>🏠 {card.address}</div>}
                    {!card.phone && !card.mobile && !card.fax && !card.email && !card.website && !card.address && <span style={{ color: '#999' }}>-</span>}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {card.memo ? (
                      <div style={{ fontSize: '13px', lineHeight: '1.4', color: '#666' }}>
                        {card.memo}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {card.tags && card.tags.length > 0 ? (
                      <div>
                        {card.tags.map((tag, index) => (
                          <span
                            key={index}
                            style={{
                              display: 'inline-block',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              marginRight: '4px',
                              marginBottom: '2px'
                            }}
                          >
                            {typeof tag === 'string' ? tag : (tag as any).name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                      <button
                        onClick={() => setEditingCard(card)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          minWidth: '50px'
                        }}
                      >
                        編集
                      </button>
                      <button
                        onClick={() => handleDeleteCard(card.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          minWidth: '50px'
                        }}
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* エラー表示 */}
      {error && (
        <div style={{
          backgroundColor: '#fee',
          color: '#c33',
          padding: '10px',
          borderRadius: '6px',
          marginTop: '20px',
          border: '1px solid #fcc'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#c33',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ×
          </button>
        </div>
      )}
      </div>

      {/* 新規作成フォーム */}
      {showCreateForm && (
        <BusinessCardForm
          onSave={handleCreateCard}
          onCancel={() => setShowCreateForm(false)}
          loading={saveLoading}
          allCards={allCards}
        />
      )}

      {/* 編集フォーム */}
      {editingCard && (
        <BusinessCardForm
          card={editingCard}
          onSave={handleUpdateCard}
          onCancel={() => setEditingCard(null)}
          loading={saveLoading}
          allCards={allCards}
        />
      )}

      {/* 画像ポップアップ */}
      <ImagePopup
        card={selectedCard}
        isOpen={showImagePopup}
        onClose={handleCloseImagePopup}
      />
    </div>
  );
};

export default BusinessCardsList;